import axios from "axios";
import {
  clearAuthSession,
  clearFournisseurSession,
  getAuthToken,
  getFournisseurToken,
  markSessionExpired,
} from "../utils/auth";

const api = axios.create({
  baseURL: "/api/",
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      markSessionExpired();
      clearAuthSession();
      if (!window.location.pathname.startsWith("/fournisseur")) {
        window.location.href = "/";
      }
    }
    return Promise.reject(error);
  }
);

// ======== AUTH =========
export const loginUser = (matricule, password) =>
  api.post("auth/login/", { matricule, password });

export const logoutUser = () => api.post("auth/logout/");

// ======== ADHERENTS =========
export const getAdherents = (params = {}) => api.get("adherents/", { params });
export const getAdherent = (id) => api.get(`adherents/${id}/`);
export const createAdherent = (data) => api.post("adherents/", data);
export const updateAdherent = (id, data) => api.put(`adherents/${id}/`, data);
export const deleteAdherent = (id) => api.delete(`adherents/${id}/`);
export const getProfile = () => api.get("adherents/profile/");
export const createParent = (data) => api.post("parents/", data);
export const createEnfant = (data) => api.post("enfants/", data);
export const createConjoint = (data) => api.post("conjoints/", data);
export const createFamilyChangeRequest = (data) => api.post("family-change-requests/", data);
export const getFamilyChangeRequests = (params = {}) =>
  api.get("family-change-requests/", { params });
export const getFamilyChangeRequestsSummary = () =>
  api.get("family-change-requests/summary/");
export const approveFamilyChangeRequest = (id, admin_note = "", payload = undefined) =>
  api.post(`family-change-requests/${id}/approve/`, payload === undefined ? { admin_note } : { admin_note, payload });
export const rejectFamilyChangeRequest = (id, admin_note = "") =>
  api.post(`family-change-requests/${id}/reject/`, { admin_note });
export const importAdherentsCsv = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post("adherents/import-csv/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// ======== NOTIFICATIONS =========
export const getNotifications = (params = {}) => api.get("notifications/", { params });
export const getNotificationsSummary = () => api.get("notifications/summary/");
export const markNotificationRead = (id) => api.post(`notifications/${id}/mark-read/`);
export const markAllNotificationsRead = () => api.post("notifications/mark-all-read/");
export const getAuditLogs = (params = {}) => api.get("audit-logs/", { params });

// ======== CHECK APIs =========
export const checkMatricule = (matricule) =>
  api.get("adherents/check-matricule/", { params: { matricule } });

export const checkTelephone = (telephone) =>
  api.get("adherents/check-telephone/", { params: { telephone } });

export const checkCin = (cin) =>
  api.get("adherents/check-cin/", { params: { cin } });

// ======== FONCTIONS =========
export const getFonctions = () => api.get("fonctions/");
export const createFonction = (data) => api.post("fonctions/", data);
export const updateFonction = (id, data) => api.put(`fonctions/${id}/`, data);
export const deleteFonction = (id) => api.delete(`fonctions/${id}/`);

// ======== GARES =========
export const getGares = () => api.get("gares/");
export const createGare = (data) => api.post("gares/", data);
export const updateGare = (id, data) => api.put(`gares/${id}/`, data);
export const deleteGare = (id) => api.delete(`gares/${id}/`);

// ======== DISTRICTS =========
export const getDistricts = () => api.get("districts/");
export const createDistrict = (data) => api.post("districts/", data);
export const updateDistrict = (id, data) => api.put(`districts/${id}/`, data);
export const deleteDistrict = (id) => api.delete(`districts/${id}/`);

// ======== EVENTS =========
export const getEvents = (params = {}) => api.get("events/", { params });
export const getEvent = (id) => api.get(`events/${id}/`);
export const createEvent = (data) => api.post("events/", data);
export const updateEvent = (id, data) => api.patch(`events/${id}/`, data);
export const deleteEvent = (id) => api.delete(`events/${id}/`);

// ======== REGISTRATIONS =========
export const createRegistration = (data) => api.post("registrations/", data);
export const getRegistrations = (params = {}) => api.get("registrations/", { params });
export const updateRegistration = (id, data) => api.patch(`registrations/${id}/`, data);
export const deleteRegistration = (id) => api.delete(`registrations/${id}/`);

// ======== FOURNISSEUR TOKEN =========
export const fapi = axios.create({
  baseURL: "/api/",
});

fapi.interceptors.request.use((config) => {
  const ft = getFournisseurToken();
  if (ft) config.headers.Authorization = `FournisseurToken ${ft}`;
  return config;
});

fapi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      markSessionExpired();
      clearFournisseurSession();
      window.location.href = "/fournisseur/login";
    }
    return Promise.reject(error);
  }
);

// ======== FOURNISSEUR (ADMIN) =========
export const createFournisseur = (data) => api.post("fournisseurs/create/", data);
export const getFournisseurs = () => api.get("fournisseurs/");
export const updateFournisseur = (id, data) => api.put(`fournisseurs/${id}/`, data);
export const deleteFournisseur = (id) => api.delete(`fournisseurs/${id}/`);

// ======== FOURNISSEUR (AUTH) =========
export const loginFournisseur = (login, password) =>
  axios.post("/api/fournisseurs/login/", { login, password });

export const changeFournisseurPassword = (old_password, new_password) =>
  fapi.post("fournisseurs/change-password/", { old_password, new_password });

export const resetFournisseurPassword = (id, new_password = null) =>
  api.post(`fournisseurs/${id}/reset-password/`, new_password ? { new_password } : {});

// ======== FORM TEMPLATES (ADMIN) =========
export const getFormTemplates = (params = {}) => api.get("form-templates/", { params });
export const getFormTemplate = (id) => api.get(`form-templates/${id}/`);
export const createFormTemplate = (data) => api.post("form-templates/", data);
export const updateFormTemplate = (id, data) => api.put(`form-templates/${id}/`, data);
export const deleteFormTemplate = (id) => api.delete(`form-templates/${id}/`);

// ======== ENGAGEMENTS (ADMIN) =========
export const getEngagements = (params = {}) => api.get("engagements/", { params });
export const getEngagement = (id) => api.get(`engagements/${id}/`);
export const createEngagement = (data) => api.post("engagements/", data);
export const updateEngagement = (id, data) => api.patch(`engagements/${id}/`, data);
export const deleteEngagement = (id) => api.delete(`engagements/${id}/`);

// ======== ENGAGEMENTS (ADHERENT) =========
export const getAvailableEngagements = () => api.get("engagements/available/");

export const submitEngagement = (engagementId, values, files = {}) => {
  const formData = new FormData();
  formData.append("data", JSON.stringify(values));

  Object.entries(files).forEach(([fieldKey, file]) => {
    if (file) {
      formData.append(`files_${fieldKey}`, file);
    }
  });

  return api.post(`engagements/${engagementId}/submit/`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const getTemplateByFournisseur = (fournisseurId) => {
  return api.get(`form-templates/?fournisseur=${fournisseurId}`);
};

export const getEngagementSubmissions = () => api.get("engagements/submissions/");

export const updateEngagementSubmissionStatus = (submissionId, statusValue) =>
  api.patch(`engagements/submissions/${submissionId}/status/`, {
    status: statusValue,
  });

export const approveEngagementSubmissionForSupplier = (submissionId) =>
  api.patch(`engagements/submissions/${submissionId}/approve/`);

export const sendEngagementSubmissionSupplierEmail = (submissionId, email) =>
  api.post(`engagements/submissions/${submissionId}/send-supplier-email/`, { email });

export const getSupplierEngagementSubmissions = () =>
  fapi.get("engagements/supplier-submissions/");

export const respondSupplierEngagementSubmission = (submissionId, accepted, reason = "") =>
  fapi.patch(`engagements/supplier-submissions/${submissionId}/response/`, {
    accepted,
    reason,
  });

export default api;
